import React, { useState } from 'react';
import { useLanguage } from '../../../../hooks/useLanguage';
import { appData as portfolioData, getPortfolioText } from '../../../../data/portfolioData';
import { appData } from '../../../../data/appData';
import './Contact.css';

const Contact = () => {
  const { getText, currentLanguage } = useLanguage();
  
  // Obtener los datos de contact del appData usando el idioma actual
  const contactData = appData.languages[currentLanguage]?.portfolio?.contact;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const whatsappMessage = `${getText('portfolio.contact.form.name')}: ${formData.name}
    ${getText('portfolio.contact.form.email')}: ${formData.email}
    ${getText('portfolio.contact.form.subject')}: ${formData.subject}
    ${getText('portfolio.contact.form.message')}: ${formData.message}`;
    
    const encodedMessage = encodeURIComponent(whatsappMessage);
    const whatsappURL = `${portfolioData.personal.whatsapp}?text=${encodedMessage}`;
    window.open(whatsappURL, '_blank');
  };

  return (
    <section className="contact-section" id="contact">
      <div className="contact-container">
        <h2 className="contact-title">{getText('portfolio.contact.title')}</h2>
        <p className="contact-subtitle">{getText('portfolio.contact.subtitle')}</p>
        
        <div className="contact-content">
          <div className="contact-info">
            <h3>{getText('portfolio.contact.info.title')}</h3>
            
            <div className="contact-item">
              <span className="contact-icon">📧</span>
              <div className="contact-details">
                <strong>{getText('portfolio.contact.info.email')}:</strong>
                <a href={`mailto:${portfolioData.personal.email}`}>{portfolioData.personal.email}</a>
              </div>
            </div>

            <div className="contact-item">
              <span className="contact-icon">💬</span>
              <div className="contact-details">
                <strong>{getText('portfolio.contact.info.whatsapp')}:</strong>
                <a href={portfolioData.personal.whatsapp} target="_blank" rel="noopener noreferrer">
                  {portfolioData.personal.phone}
                </a>
              </div>
            </div>

            <div className="contact-item">
              <span className="contact-icon">📍</span>
              <div className="contact-details">
                <strong>{getText('portfolio.contact.info.location')}:</strong>
                <span>{portfolioData.personal.location.full}</span>
              </div>
            </div>

            <div className="contact-item">
              <span className="contact-icon">🕒</span>
              <div className="contact-details">
                <strong>{getText('portfolio.contact.info.availability')}:</strong>
                <span>{getPortfolioText(portfolioData.personal.availability, currentLanguage)}</span>
              </div>
            </div>

            <div className="social-section">
              <h4>{getText('portfolio.contact.social.title')}</h4>
              <div className="social-links">
                {portfolioData.social.map((social, index) => (
                  <a 
                    key={index}
                    href={social.url} 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link"
                  >
                    <span className="social-icon">{social.icon}</span>
                    <span className="social-name">{social.platform}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="contact-form-container">
            <h3>{getText('portfolio.contact.form.title')}</h3>
            
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">{getText('portfolio.contact.form.name')} *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder={getText('portfolio.contact.form.namePlaceholder')}
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">{getText('portfolio.contact.form.email')} *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder={getText('portfolio.contact.form.emailPlaceholder')}
                />
              </div>

              <div className="form-group">
                <label htmlFor="subject">{getText('portfolio.contact.form.subject')} *</label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">{getText('portfolio.contact.form.selectSubject')}</option>
                  <option value={getText('portfolio.contact.form.subjects.webDev')}>{getText('portfolio.contact.form.subjects.webDev')}</option>
                  <option value={getText('portfolio.contact.form.subjects.consulting')}>{getText('portfolio.contact.form.subjects.consulting')}</option>
                  <option value={getText('portfolio.contact.form.subjects.collaboration')}>{getText('portfolio.contact.form.subjects.collaboration')}</option>
                  <option value={getText('portfolio.contact.form.subjects.other')}>{getText('portfolio.contact.form.subjects.other')}</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message">{getText('portfolio.contact.form.message')} *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows="5"
                  placeholder={getText('portfolio.contact.form.messagePlaceholder')}
                ></textarea>
              </div>

              <button type="submit" className="submit-btn">
                💬 {getText('portfolio.contact.form.sendButton')}
              </button>
            </form>
          </div>
        </div>

        <div className="services-available">
          <h3>{contactData?.services?.title || getText('portfolio.contact.services.title')}</h3>
          <ul className="services-list">
            {contactData?.services?.items?.map((service, index) => (
              <li key={index}>{service}</li>
            )) || null}
          </ul>
        </div>

        <div className="quick-contact">
          <p>{getText('portfolio.contact.quickContact.title')}</p>
          <div className="quick-buttons">
            <a 
              href={portfolioData.personal.whatsapp} 
              target="_blank" 
              rel="noopener noreferrer"
              className="quick-btn whatsapp"
            >
              💬 {getText('portfolio.contact.quickContact.whatsapp')}
            </a>
            <a 
              href={`mailto:${portfolioData.personal.email}`}
              className="quick-btn email"
            >
              📧 {getText('portfolio.contact.quickContact.email')}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
